import * as React from 'react';
import { noop, pick } from 'lodash';
import DockingTabBar, { Tab } from '@splunk/react-docs/DockingTabBar';
import APIPage from '@splunk/react-docs/API';
import previewVizMessage from './previewVizMessage';

interface ComponentPageProps {
    createComponentURL: (componentName: string) => string;
    dependsOnSplunk: boolean;
    status: string;
    description: string;
    overviewSection: React.ReactNode;
    conceptsSection: React.ReactNode;
    examplesSection: React.ReactNode;
    optionSection: React.ReactNode;
    encodingSection: React.ReactNode;
    eventSection: React.ReactNode;
    themeSection: React.ReactNode;
    headerOffset: number;
    sourceURL: string;
    title: string;
}

interface ComponentPageState {
    activeSection: string;
}

class ComponentPage extends React.Component<ComponentPageProps, ComponentPageState> {
    static currentSection = 'overview';

    bodyEl: any;

    constructor(args) {
        super(args);
        this.state = { activeSection: ComponentPage.currentSection };
    }

    handleBodyMount = el => {
        this.bodyEl = el;
    };

    handleSectionChange = (e, { selectedTabId }) => {
        ComponentPage.currentSection = selectedTabId;
        this.setState({ activeSection: selectedTabId }, () => {
            // After the new section loads, scroll to the top of the section.
            const { top } = this.bodyEl.getBoundingClientRect();
            if (top < window.scrollY) {
                const target = top + window.scrollY - 90;
                window.scroll(0, target);
            }
        });
    };

    renderBody() {
        const {
            encodingSection,
            optionSection,
            overviewSection,
            conceptsSection,
            examplesSection,
            eventSection,
            themeSection,
        } = this.props;
        const { activeSection } = this.state;
        switch (activeSection) {
            case 'concepts':
                return conceptsSection;
            case 'overview':
                return overviewSection;
            case 'examples':
                return examplesSection;
            case 'encoding':
                return encodingSection;
            case 'option':
                return optionSection;
            case 'event':
                return eventSection;
            case 'theme':
                return themeSection;
            default:
                return null;
        }
    }

    render() {
        const {
            createComponentURL = noop,
            conceptsSection,
            overviewSection,
            examplesSection,
            encodingSection,
            eventSection,
            themeSection,
            headerOffset = 0,
            sourceURL,
        } = this.props;
        let basePageProps = pick(this.props, 'dependsOnSplunk', 'description', 'title');

        if (this.props.status === 'preview') {
            basePageProps = {
                ...basePageProps,
                description: `#### ${previewVizMessage} \n ${basePageProps.description || ''}`,
            };
        }

        let { activeSection } = this.state;
        let body = this.renderBody();

        if (!body) {
            body = examplesSection;
            activeSection = 'examples';
        }
        return (
            <APIPage sourceURL={sourceURL || createComponentURL(window.location.pathname)} {...basePageProps}>
                <DockingTabBar
                    activeTabId={activeSection}
                    offset={headerOffset}
                    onChange={this.handleSectionChange}
                >
                    {overviewSection && <Tab label="Overview" tabId="overview" />}
                    <Tab label="Examples" tabId="examples" />
                    {encodingSection && <Tab label="Encoding" tabId="encoding" />}
                    <Tab label="Options" tabId="option" />
                    {eventSection && <Tab label="Events" tabId="event" />}
                    {themeSection && <Tab label="Themes" tabId="theme" />}
                    {conceptsSection && <Tab label="Concepts" tabId="concepts" />}
                </DockingTabBar>
                <div
                    style={{
                        padding: '0 30px 30px',
                        marginTop: '20px',
                    }}
                    ref={this.handleBodyMount}
                >
                    {body}
                </div>
            </APIPage>
        );
    }
}

export default ComponentPage;
